import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Header from "@/components/Header";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Leaf, Sparkles, Loader2, Copy, AlertCircle, ChefHat, ArrowRight, Info, Crown, ClipboardCheck,
} from "lucide-react";

const DIETS = [
  { key: "gluten-free",         label: "Gluten-Free",         emoji: "🌾", color: "#B5834B" },
  { key: "vegan",               label: "Vegan",               emoji: "🌱", color: "#4C9F70" },
  { key: "keto",                label: "Keto / Low-Carb",     emoji: "🥑", color: "#8EAC50" },
  { key: "dairy-free",          label: "Dairy-Free",          emoji: "🥛", color: "#2F80ED" },
  { key: "nut-free",            label: "Nut-Free",            emoji: "🌰", color: "#7A5230" },
  { key: "low-sodium",          label: "Low-Sodium",          emoji: "🧂", color: "#C2185B" },
  { key: "refined-sugar-free",  label: "Refined-Sugar-Free",  emoji: "🍯", color: "#E8842B" },
  { key: "custom",              label: "Custom…",             emoji: "✍️", color: "#8E7CC3" },
];

export default function DietaryAdaptor() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [recipeName, setRecipeName] = useState("");
  const [recipeText, setRecipeText] = useState("");
  const [diet, setDiet] = useState("gluten-free");
  const [customInstructions, setCustomInstructions] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [quota, setQuota] = useState(null);
  const [savedRecipes, setSavedRecipes] = useState([]);

  useEffect(() => {
    if (loading) return;
    if (!user || user === false) { nav("/login"); return; }
    api.get("/adaptor/quota").then((r) => setQuota(r.data));
    api.get("/recipes/mine").then((r) => setSavedRecipes(r.data)).catch(() => {});
  }, [user, loading, nav]);

  const loadSaved = (id) => {
    if (!id) return;
    const r = savedRecipes.find((r) => r.id === id);
    if (!r) return;
    setRecipeName(r.name);
    const lines = (r.ingredients || []).map((i) => `- ${i.name || "(unnamed)"}: ${i.amount}${i.unit}`);
    setRecipeText(`Yields: ${r.servings} servings\n\nIngredients:\n${lines.join("\n")}`);
    toast.success(`Loaded "${r.name}"`);
  };

  const convert = async () => {
    if (recipeText.trim().length < 10) { toast.error("Paste a recipe first (at least 10 characters)"); return; }
    if (quota && !quota.is_premium && quota.remaining <= 0) { toast.error("Free tier limit reached. Upgrade to premium for unlimited."); return; }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/adaptor/convert", {
        recipe_text: recipeText,
        recipe_name: recipeName || null,
        diet,
        custom_instructions: customInstructions || null,
      }, { timeout: 120000 });
      setResult(data);
      api.get("/adaptor/quota").then((r) => setQuota(r.data));
      toast.success("Recipe adapted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const copyAdapted = () => {
    if (!result) return;
    const a = result.adapted;
    const lines = [];
    lines.push(a.adapted_name || `${result.diet} adaptation`);
    lines.push("");
    lines.push("== Ingredient swaps ==");
    for (const s of (a.substitutions || [])) {
      lines.push(`• ${s.original} → ${s.replacement}`);
      if (s.rationale) lines.push(`   (${s.rationale})`);
    }
    if (a.additional_ingredients?.length) {
      lines.push("");
      lines.push("== Add ==");
      for (const i of a.additional_ingredients) lines.push(`• ${i}`);
    }
    if (a.removed_ingredients?.length) {
      lines.push("");
      lines.push("== Remove ==");
      for (const i of a.removed_ingredients) lines.push(`• ${i}`);
    }
    if (a.method_adjustments?.length) {
      lines.push("");
      lines.push("== Method changes ==");
      for (const i of a.method_adjustments) lines.push(`• ${i}`);
    }
    if (a.flavor_notes) {
      lines.push("");
      lines.push("== Flavor & texture ==");
      lines.push(a.flavor_notes);
    }
    if (a.warnings?.length) {
      lines.push("");
      lines.push("== Warnings ==");
      for (const w of a.warnings) lines.push(`• ${w}`);
    }
    navigator.clipboard.writeText(lines.join("\n"));
    toast.success("Adapted recipe copied");
  };

  if (loading || !user) return null;
  const a = result?.adapted;
  const activeDiet = DIETS.find((d) => d.key === diet) || DIETS[0];
  const remaining = quota?.is_premium ? "unlimited" : `${quota?.remaining ?? "…"} of ${quota?.limit ?? 3}`;

  return (
    <>
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 lg:py-10">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#857163]">
              <Leaf className="w-3.5 h-3.5" style={{ color: "#4C9F70" }} />
              <span>Baker's tool · premium</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#3B2C24] mt-1">
              Dietary <span style={{ color: "#4C9F70" }}>Adaptor</span>
            </h1>
            <p className="mt-2 text-[#857163] max-w-2xl">
              Convert any recipe to gluten-free, vegan, keto, or another diet. AI suggests smart ingredient swaps, method tweaks, and calls out texture trade-offs so you know what to expect.
            </p>
          </div>
          <div className="text-right shrink-0">
            <div className="text-xs uppercase tracking-widest text-[#857163]">This month</div>
            <div className="font-heading text-2xl text-[#3B2C24] mt-0.5">
              {quota?.is_premium ? <span className="inline-flex items-center gap-1"><Crown className="w-5 h-5" style={{ color: "#F4A83B" }} /> Unlimited</span> : remaining}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Left: input */}
          <div className="cp-card p-5 sm:p-6 space-y-4">
            {savedRecipes.length > 0 && (
              <div>
                <label className="text-xs text-[#857163]">Load from My Recipes (optional)</label>
                <select
                  className="cp-input mt-1"
                  onChange={(e) => loadSaved(e.target.value)}
                  defaultValue=""
                  data-testid="load-saved-select"
                >
                  <option value="">— pick a saved recipe —</option>
                  {savedRecipes.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
            )}

            <div>
              <label className="text-xs text-[#857163]">Recipe name (optional)</label>
              <input
                className="cp-input mt-1"
                value={recipeName}
                onChange={(e) => setRecipeName(e.target.value)}
                placeholder="e.g. Grandma's Buttermilk Pancakes"
                data-testid="recipe-name-input"
              />
            </div>

            <div>
              <label className="text-xs text-[#857163]">Recipe (paste ingredients + method)</label>
              <textarea
                className="cp-input mt-1 min-h-[220px] resize-y font-mono text-xs leading-relaxed"
                value={recipeText}
                onChange={(e) => setRecipeText(e.target.value)}
                placeholder="Paste your full recipe here — ingredients with amounts + a rough method.&#10;&#10;Example:&#10;- 2 cups all-purpose flour&#10;- 1 cup buttermilk&#10;- 2 eggs&#10;- 3 tbsp butter, melted&#10;- 2 tbsp sugar&#10;- 1 tsp baking powder&#10;- 1/2 tsp salt&#10;&#10;Whisk dry, whisk wet, combine and cook on hot griddle."
                data-testid="recipe-text-input"
              />
              <div className="text-[10px] text-[#857163] mt-1">{recipeText.length} chars — the more detail, the better the adaptation.</div>
            </div>

            <div>
              <label className="text-xs text-[#857163] mb-2 block">Adapt to</label>
              <div className="flex flex-wrap gap-2">
                {DIETS.map((d) => (
                  <button
                    key={d.key}
                    onClick={() => setDiet(d.key)}
                    className={`px-3 py-2 rounded-full text-sm font-medium border-2 transition ${
                      diet === d.key ? "text-white shadow" : "bg-white text-[#3B2C24] hover:bg-[#F9EFE6]"
                    }`}
                    style={{
                      background: diet === d.key ? d.color : "",
                      borderColor: diet === d.key ? d.color : "#EBDCD1",
                    }}
                    data-testid={`diet-${d.key}`}
                  >
                    <span className="mr-1">{d.emoji}</span>{d.label}
                  </button>
                ))}
              </div>
            </div>

            {diet === "custom" && (
              <div>
                <label className="text-xs text-[#857163]">Your custom instructions</label>
                <textarea
                  className="cp-input mt-1 min-h-[80px] resize-y"
                  value={customInstructions}
                  onChange={(e) => setCustomInstructions(e.target.value)}
                  placeholder="e.g. 'Make it low-FODMAP', 'Substitute eggs with flax', 'Reduce sugar by half'"
                  data-testid="custom-instructions-input"
                />
              </div>
            )}

            <button
              onClick={convert}
              disabled={busy || recipeText.trim().length < 10 || (quota && !quota.is_premium && quota.remaining <= 0)}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-white font-semibold text-base transition hover:opacity-95 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: `linear-gradient(135deg, ${activeDiet.color} 0%, ${activeDiet.color}CC 100%)` }}
              data-testid="convert-btn"
            >
              {busy ? (<><Loader2 className="w-5 h-5 animate-spin" /> Adapting your recipe…</>) : (<><Sparkles className="w-5 h-5" /> Adapt to {activeDiet.label}</>)}
            </button>

            <div className="text-[11px] text-[#857163] leading-relaxed flex items-start gap-1.5">
              <Info className="w-3 h-3 mt-0.5 shrink-0" />
              <span>AI-suggested adaptations. Always test in a small batch first — substitutions can shift texture, rise, and browning.</span>
            </div>
          </div>

          {/* Right: results */}
          <div className="cp-card p-5 sm:p-6 min-h-[400px]" data-testid="results-panel">
            {!result && !busy && (
              <div className="h-full flex flex-col items-center justify-center text-center py-16">
                <div className="w-16 h-16 rounded-full grid place-items-center mb-4" style={{ background: `${activeDiet.color}20` }}>
                  <ChefHat className="w-7 h-7" style={{ color: activeDiet.color }} />
                </div>
                <div className="font-heading text-2xl text-[#3B2C24]">Ready to convert</div>
                <p className="text-sm text-[#857163] mt-2 max-w-sm">
                  Paste your recipe, pick a diet, and hit <b>Adapt</b>. You'll get ingredient swaps, method tweaks, and flavor notes.
                </p>
              </div>
            )}
            {busy && (
              <div className="h-full flex flex-col items-center justify-center py-16">
                <Loader2 className="w-10 h-10 animate-spin" style={{ color: activeDiet.color }} />
                <div className="mt-4 font-heading text-lg text-[#3B2C24]">Rewriting for {activeDiet.label}…</div>
                <div className="text-xs text-[#857163] mt-1">5–15 seconds</div>
              </div>
            )}
            {result && a && (
              <div className="space-y-4" data-testid="adaptor-result">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#857163] flex items-center gap-1.5">
                      <span>{activeDiet.emoji}</span> {activeDiet.label} adaptation
                    </div>
                    <h2 className="font-heading text-2xl text-[#3B2C24] mt-1 leading-tight">{a.adapted_name || "Adapted recipe"}</h2>
                    {a.difficulty && (
                      <span className="cp-tag mt-1 inline-block" style={{ background: `${activeDiet.color}22`, color: activeDiet.color }}>
                        {a.difficulty} adaptation
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col items-end shrink-0">
                    <div className="text-xs text-[#857163]">Confidence</div>
                    <div className="font-heading text-3xl font-bold" style={{ color: activeDiet.color }}>{a.confidence ?? "—"}%</div>
                    <button
                      onClick={copyAdapted}
                      className="mt-1 inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold text-white hover:opacity-90 transition"
                      style={{ background: activeDiet.color }}
                      data-testid="copy-adapted-btn"
                    ><Copy className="w-3 h-3" /> Copy</button>
                  </div>
                </div>

                {a.substitutions?.length > 0 && (
                  <div>
                    <div className="text-sm font-semibold text-[#3B2C24] mb-2">Ingredient swaps</div>
                    <div className="space-y-2">
                      {a.substitutions.map((s, i) => (
                        <div key={i} className="p-3 rounded-2xl border border-[#EBDCD1] bg-[#F9EFE6]/40">
                          <div className="flex items-center gap-2 flex-wrap text-sm">
                            <span className="line-through text-[#857163]">{s.original}</span>
                            <ArrowRight className="w-3.5 h-3.5" style={{ color: activeDiet.color }} />
                            <span className="font-semibold text-[#3B2C24]">{s.replacement}</span>
                          </div>
                          {s.rationale && <div className="text-xs text-[#857163] mt-1 italic">↳ {s.rationale}</div>}
                          {s.amount_change && s.amount_change !== "no change" && (
                            <div className="text-xs text-[#3B2C24] mt-1">⚖️ {s.amount_change}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {a.additional_ingredients?.length > 0 && (
                  <ListBlock title="Add these" items={a.additional_ingredients} accent="#4C9F70" prefix="+" />
                )}
                {a.removed_ingredients?.length > 0 && (
                  <ListBlock title="Remove" items={a.removed_ingredients} accent="#C2185B" prefix="−" />
                )}
                {a.method_adjustments?.length > 0 && (
                  <ListBlock title="Method changes" items={a.method_adjustments} accent="#F4A83B" prefix="→" />
                )}

                {a.flavor_notes && (
                  <div className="p-3 rounded-2xl" style={{ background: `${activeDiet.color}12` }}>
                    <div className="text-sm font-semibold text-[#3B2C24] mb-1 inline-flex items-center gap-1.5">
                      <ClipboardCheck className="w-4 h-4" style={{ color: activeDiet.color }} /> Flavor & texture
                    </div>
                    <p className="text-sm text-[#3B2C24]">{a.flavor_notes}</p>
                  </div>
                )}

                {a.warnings?.length > 0 && (
                  <div className="p-3 rounded-2xl border-l-4 border-[#C2185B] bg-[#C2185B]/5">
                    <div className="text-sm font-semibold text-[#3B2C24] mb-1 inline-flex items-center gap-1.5">
                      <AlertCircle className="w-4 h-4" style={{ color: "#C2185B" }} /> Heads up
                    </div>
                    <ul className="space-y-0.5">
                      {a.warnings.map((w, i) => (
                        <li key={i} className="text-sm text-[#3B2C24] flex gap-2"><span style={{ color: "#C2185B" }}>!</span>{w}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
}

function ListBlock({ title, items, accent, prefix }) {
  return (
    <div>
      <div className="text-sm font-semibold text-[#3B2C24] mb-2">{title}</div>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="text-sm text-[#3B2C24] flex gap-2">
            <span className="font-bold shrink-0 w-4" style={{ color: accent }}>{prefix}</span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
